import { useEffect, useState } from 'react';
import { Card } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { DollarSign, CreditCard, Users as UsersIcon, Building2, TrendingUp } from 'lucide-react';
import axios from 'axios';
import { API } from '../../App';

const Metric = ({ icon: Icon, label, value, accent = 'text-emerald-400' }) => (
  <Card className="glass p-4 border border-stone-800">
    <Icon className={`w-6 h-6 ${accent} mb-1`} />
    <p className="text-stone-400 text-xs uppercase tracking-wider">{label}</p>
    <p className="text-2xl font-bold text-white">{value}</p>
  </Card>
);

const AdminPayments = ({ stats, users }) => {
  const [orgs, setOrgs] = useState([]);
  const [search, setSearch] = useState('');

  useEffect(() => {
    axios.get(`${API}/admin/organizations`).then((r) => setOrgs(r.data.organizations || [])).catch(() => {});
  }, []);

  const paidUsers = (users || []).filter((u) => u.payment?.is_paid);
  const filteredPaid = paidUsers.filter((u) =>
    !search || (u.email || '').toLowerCase().includes(search.toLowerCase()) || (u.username || '').toLowerCase().includes(search.toLowerCase())
  );
  const payingOrgs = orgs.filter((o) => o.monthly_price > 0 && o.subscription_status === 'active');
  const orgMrr = payingOrgs.reduce((s, o) => s + o.monthly_price, 0);

  return (
    <div data-testid="admin-payments-tab">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <Metric icon={UsersIcon} label="Paying App Users" value={stats?.paying_users ?? 0} />
        <Metric icon={DollarSign} label="App Revenue" value={`$${(stats?.individual_revenue ?? 0).toFixed(2)}`} />
        <Metric icon={Building2} label="Paying Orgs" value={payingOrgs.length} accent="text-amber-400" />
        <Metric icon={TrendingUp} label="Org MRR (est.)" value={`$${orgMrr}`} accent="text-amber-400" />
      </div>

      {/* Paid individual users */}
      <Card className="glass p-4 border border-stone-800 mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-4">
          <h2 className="text-lg font-bold text-emerald-400 tracking-wider flex items-center gap-2"><CreditCard className="w-5 h-5" />PAID APP USERS</h2>
          <Input placeholder="Search paid users..." value={search} onChange={(e) => setSearch(e.target.value)} className="bg-stone-900 border-stone-700 text-white sm:w-64" data-testid="paid-user-search" />
        </div>
        {filteredPaid.length === 0 ? (
          <p className="text-stone-500 text-sm py-4">No paying individual users yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[10px] uppercase tracking-widest text-stone-500 border-b border-stone-800">
                  <th className="text-left py-2 pr-3">User</th>
                  <th className="text-left py-2 px-2">Email</th>
                  <th className="text-right py-2 px-2">Total Paid</th>
                  <th className="text-right py-2 px-2">Payments</th>
                  <th className="text-left py-2 pl-2">Last Plan</th>
                  <th className="text-left py-2 pl-2">Last Payment</th>
                </tr>
              </thead>
              <tbody>
                {filteredPaid.map((u) => (
                  <tr key={u.id} className="border-b border-stone-900 text-stone-300" data-testid={`paid-user-${u.id}`}>
                    <td className="py-2 pr-3 font-medium text-white">{u.username}</td>
                    <td className="py-2 px-2">{u.email}</td>
                    <td className="py-2 px-2 text-right text-emerald-400 font-bold">${(u.payment.total_paid || 0).toFixed(2)}</td>
                    <td className="py-2 px-2 text-right">{u.payment.payment_count}</td>
                    <td className="py-2 pl-2">{u.payment.last_payment?.package_type || '—'}</td>
                    <td className="py-2 pl-2">{u.payment.last_payment?.date ? new Date(u.payment.last_payment.date).toLocaleDateString() : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Paying organizations */}
      <Card className="glass p-4 border border-stone-800">
        <h2 className="text-lg font-bold text-amber-400 tracking-wider flex items-center gap-2 mb-4"><Building2 className="w-5 h-5" />PAYING ORGANIZATIONS</h2>
        {payingOrgs.length === 0 ? (
          <p className="text-stone-500 text-sm py-4">No paying organizations yet (all beta/free or enterprise).</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[10px] uppercase tracking-widest text-stone-500 border-b border-stone-800">
                  <th className="text-left py-2 pr-3">Organization</th>
                  <th className="text-left py-2 px-2">Owner</th>
                  <th className="text-left py-2 px-2">Plan</th>
                  <th className="text-right py-2 px-2">Monthly</th>
                  <th className="text-right py-2 pl-2">Athletes</th>
                </tr>
              </thead>
              <tbody>
                {payingOrgs.map((o) => (
                  <tr key={o.id} className="border-b border-stone-900 text-stone-300" data-testid={`paying-org-${o.id}`}>
                    <td className="py-2 pr-3 font-medium text-white">{o.name}</td>
                    <td className="py-2 px-2">{o.owner_email}</td>
                    <td className="py-2 px-2 uppercase text-amber-400">{o.plan_tier}</td>
                    <td className="py-2 px-2 text-right text-amber-400 font-bold">${o.monthly_price}</td>
                    <td className="py-2 pl-2 text-right">{o.athlete_count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
};

export default AdminPayments;
